import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Search, Terminal, Filter, Star, StarOff, Sparkles, FileText } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useBrand } from '@/config/brands';
import { Helmet } from "react-helmet-async";
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';

interface CommonProcedureData {
  code_type: string;
  procedure_code: string;
  hospital_count: number;
  avg_hospital_price: number;
  avg_national_price: number;
  avg_markup_pct: number;
  description?: string;
  consumer_friendly_description?: string;
  is_common?: boolean;
  procedure_id?: string;
}

export default function CommonProcedures() {
  const brand = useBrand();
  const { t } = useTranslation(['procedures', 'common']);
  const isAhp = brand.key === 'ahp';
  const [searchQuery, setSearchQuery] = useState('');
  // Render cap — the unfiltered list is ~45K rows; mapping them all built a
  // 500K-word DOM that chokes phones. Filters/search still scan everything.
  const [displayLimit, setDisplayLimit] = useState(100);
  const [selectedCodeType, setSelectedCodeType] = useState<string>('all');
  const [minHospitals, setMinHospitals] = useState(10);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Check if user is admin
  const { data: isAdmin = false } = useQuery({
    queryKey: ['user-admin-status'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('Debug: No user found');
        return false;
      }
      
      const role = user.user_metadata?.role || user.app_metadata?.role;
      console.log('Debug: User role detected:', role);
      console.log('Debug: User metadata:', user.user_metadata);
      console.log('Debug: App metadata:', user.app_metadata);
      console.log('Debug: Is admin?', role === 'admin');
      return role === 'admin';
    },
    staleTime: 300000, // 5 minutes
  });

  // Mutation to toggle common status
  const toggleCommonMutation = useMutation({
    mutationFn: async ({ procedureId, isCommon }: { procedureId: string; isCommon: boolean }) => {
      const { error } = await supabase
        .from('procedures')
        .update({ is_common: isCommon })
        .eq('id', procedureId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['common-procedures'] });
      toast({
        title: t('common:common.success'),
        description: t('procedures:toast.commonStatusUpdated'),
      });
    },
    onError: (error) => {
      toast({
        title: t('common:common.error'),
        description: t('procedures:toast.errorUpdate', { message: error.message }),
        variant: "destructive",
      });
    },
  });

  // Mutation to generate consumer-friendly names (bulk)
  const generateNamesMutation = useMutation({
    mutationFn: async ({ limit = 5, batchSize = 5, minHospitalCount = 10 }: { 
      limit?: number; 
      batchSize?: number; 
      minHospitalCount?: number; 
    }) => {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-consumer-friendly-names`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            limit,
            batch_size: batchSize,
            min_hospital_count: minHospitalCount,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    },
    onSuccess: async (data) => {
      // Force a complete refresh by invalidating and immediately refetching
      await queryClient.invalidateQueries({ queryKey: ['common-procedures'] });
      await queryClient.refetchQueries({ queryKey: ['common-procedures'] });
      toast({
        title: t('common:common.success'),
        description: t('procedures:toast.namesGenerated', { count: data.processed }),
      });
    },
    onError: (error) => {
      toast({
        title: t('common:common.error'),
        description: t('procedures:toast.errorGenerateNames', { message: error.message }),
        variant: "destructive",
      });
    },
  });

  // Mutation for individual procedure name generation
  const generateIndividualNameMutation = useMutation({
    mutationFn: async ({ procedureId, procedureCode, description }: { 
      procedureId: string; 
      procedureCode: string;
      description?: string;
    }) => {
      // Note: We don't need to clear the consumer_friendly_description anymore
      // The edge function will handle this when we pass the specific_procedure_code

      // Call the edge function with limit 1 to process this specific procedure
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-consumer-friendly-names`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            limit: 1,
            batch_size: 1,
            min_hospital_count: 1,
            specific_procedure_code: procedureCode // Add this to target specific procedure
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.processed === 0) {
        throw new Error('No consumer-friendly name was generated');
      }

      // Get the updated procedure to return the generated name
      const { data: updatedProcedure, error: fetchError } = await supabase
        .from('procedures')
        .select('consumer_friendly_description')
        .eq('id', procedureId)
        .single();

      if (fetchError) throw fetchError;

      return { 
        procedureId, 
        consumerFriendlyName: updatedProcedure.consumer_friendly_description,
        processed: data.processed
      };
    },
    onSuccess: async (data) => {
      // Force a complete refresh by invalidating and immediately refetching
      await queryClient.invalidateQueries({ queryKey: ['common-procedures'] });
      await queryClient.refetchQueries({ queryKey: ['common-procedures'] });
      toast({
        title: t('common:common.success'),
        description: t('procedures:toast.nameGenerated', { name: data.consumerFriendlyName }),
      });
    },
    onError: (error) => {
      toast({
        title: t('common:common.error'),
        description: t('procedures:toast.errorGenerateName', { message: error.message }),
        variant: "destructive",
      });
    },
  });

  const { data: procedures = [], isLoading, error } = useQuery({
    queryKey: ['common-procedures', minHospitals],
    queryFn: async (): Promise<CommonProcedureData[]> => {
      // Query pre-aggregated pricing tables (NOT the 315M row hospital_procedure_analytics)
      // Fetch CPT, HCPCS, DRG, and REV pricing in parallel
      const [cptRes, hcpcsRes, drgRes, revRes] = await Promise.all([
        supabase
          .from('cpt_pricing_averages')
          .select('cpt_code, total_hospitals, avg_gross_charge, median_gross_charge, min_gross_charge, max_gross_charge')
          .eq('setting_type', 'all_settings')
          .gte('total_hospitals', minHospitals)
          .order('total_hospitals', { ascending: false }),
        supabase
          .from('hcpcs_pricing_averages')
          .select('hcpcs_code, total_hospitals, avg_gross_charge, median_gross_charge, min_gross_charge, max_gross_charge')
          .eq('setting_type', 'all_settings')
          .gte('total_hospitals', minHospitals)
          .order('total_hospitals', { ascending: false }),
        supabase
          .from('drg_pricing_averages')
          .select('drg_code, total_hospitals, avg_gross_charge, median_gross_charge, min_gross_charge, max_gross_charge')
          .gte('total_hospitals', minHospitals)
          .order('total_hospitals', { ascending: false }),
        supabase
          .from('revenue_pricing_averages')
          .select('revenue_code, revenue_description, total_hospitals, avg_charge, median_charge, min_charge, max_charge')
          .gte('total_hospitals', minHospitals)
          .order('total_hospitals', { ascending: false }),
      ]);

      if (cptRes.error) throw cptRes.error;
      if (hcpcsRes.error) throw hcpcsRes.error;
      if (drgRes.error) throw drgRes.error;
      if (revRes.error) throw revRes.error;

      // Collect all procedure codes for descriptions lookup
      const allCodes = [
        ...(cptRes.data || []).map(r => r.cpt_code),
        ...(hcpcsRes.data || []).map(r => r.hcpcs_code),
      ];

      // Fetch procedure descriptions in batches
      let proceduresData: any[] = [];
      const batchSize = 1000;
      for (let i = 0; i < allCodes.length; i += batchSize) {
        const batch = allCodes.slice(i, i + batchSize);
        const { data: batchData } = await supabase
          .from('procedures')
          .select('id, code, name, description, consumer_friendly_description, is_common, llm_quick_name')
          .in('code', batch);
        if (batchData) proceduresData = proceduresData.concat(batchData);
      }
      const procedureMap = new Map(proceduresData.map(p => [p.code, p]));

      const result: CommonProcedureData[] = [];

      // Process CPT codes
      for (const row of (cptRes.data || [])) {
        const info = procedureMap.get(row.cpt_code);
        const avg = Number(row.avg_gross_charge) || 0;
        const median = Number(row.median_gross_charge) || avg;
        result.push({
          code_type: 'CPT',
          procedure_code: row.cpt_code,
          hospital_count: row.total_hospitals,
          avg_hospital_price: Math.round(avg),
          avg_national_price: Math.round(median),
          avg_markup_pct: median > 0 ? Math.round((avg / median - 1) * 1000) / 10 : 0,
          description: info?.llm_quick_name || info?.description || info?.name,
          consumer_friendly_description: info?.consumer_friendly_description,
          is_common: info?.is_common || false,
          procedure_id: info?.id,
        });
      }

      // Process HCPCS codes
      for (const row of (hcpcsRes.data || [])) {
        const info = procedureMap.get(row.hcpcs_code);
        const avg = Number(row.avg_gross_charge) || 0;
        const median = Number(row.median_gross_charge) || avg;
        result.push({
          code_type: 'HCPCS',
          procedure_code: row.hcpcs_code,
          hospital_count: row.total_hospitals,
          avg_hospital_price: Math.round(avg),
          avg_national_price: Math.round(median),
          avg_markup_pct: median > 0 ? Math.round((avg / median - 1) * 1000) / 10 : 0,
          description: info?.llm_quick_name || info?.description || info?.name,
          consumer_friendly_description: info?.consumer_friendly_description,
          is_common: info?.is_common || false,
          procedure_id: info?.id,
        });
      }

      // Process DRG codes
      for (const row of (drgRes.data || [])) {
        const avg = Number(row.avg_gross_charge) || 0;
        const median = Number(row.median_gross_charge) || avg;
        result.push({
          code_type: 'DRG',
          procedure_code: row.drg_code,
          hospital_count: row.total_hospitals,
          avg_hospital_price: Math.round(avg),
          avg_national_price: Math.round(median),
          avg_markup_pct: median > 0 ? Math.round((avg / median - 1) * 1000) / 10 : 0,
          description: (row as any).procedure_description || undefined,
          is_common: false,
        });
      }

      // Process REV codes
      for (const row of (revRes.data || [])) {
        const avg = Number(row.avg_charge) || 0;
        const median = Number(row.median_charge) || avg;
        result.push({
          code_type: 'REV',
          procedure_code: row.revenue_code,
          hospital_count: row.total_hospitals,
          avg_hospital_price: Math.round(avg),
          avg_national_price: Math.round(median),
          avg_markup_pct: median > 0 ? Math.round((avg / median - 1) * 1000) / 10 : 0,
          description: row.revenue_description || undefined,
          is_common: false,
        });
      }

      return result.sort((a, b) => {
        if (a.is_common && !b.is_common) return -1;
        if (!a.is_common && b.is_common) return 1;
        return b.hospital_count - a.hospital_count;
      });
    },
    staleTime: 300000, // 5 minutes
  });

  const filteredProcedures = procedures.filter(procedure => {
    const matchesSearch = searchQuery === '' || 
      procedure.procedure_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (procedure.description && procedure.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (procedure.consumer_friendly_description && procedure.consumer_friendly_description.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCodeType = selectedCodeType === 'all' || procedure.code_type === selectedCodeType;
    return matchesSearch && matchesCodeType;
  });

  const getCodeTypeColor = (codeType: string): string => {
    switch (codeType) {
      case 'CPT': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'DRG': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'REV': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'HCPCS': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'SUP': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'RX': return 'bg-pink-500/20 text-pink-400 border-pink-500/30';
      case 'NDC': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getMarkupColor = (markup: number): string => {
    if (markup <= -10) return 'text-green-400';
    if (markup < 10) return 'text-gray-400';
    if (markup < 50) return 'text-orange-400';
    return 'text-red-400';
  };

  const codeTypeCounts = procedures.reduce((acc, proc) => {
    acc[proc.code_type] = (acc[proc.code_type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-red-400 mb-4">{t('procedures:error.title')}</h1>
          <p className="text-gray-400">{t('procedures:error.description')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>{t('procedures:meta.pageTitle', { brandName: brand.name })}</title>
        <meta name="description" content={t('procedures:meta.description')} />
        <meta name="keywords" content="common medical procedures, procedure prices, surgery costs, medical procedure comparison, hospital procedure pricing, common surgery prices" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${brand.primaryDomain}/common-procedures`} />
        <meta property="og:title" content={t('procedures:meta.ogTitle')} />
        <meta property="og:description" content={t('procedures:meta.description')} />
        <meta property="og:image" content={brand.social.ogImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${brand.primaryDomain}/common-procedures`} />
        <meta property="og:site_name" content={brand.name} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('procedures:meta.ogTitle')} />
        <meta name="twitter:description" content={t('procedures:meta.description')} />
        <meta name="twitter:image" content={brand.social.ogImage} />
        {brand.social.twitter && <meta name="twitter:site" content={brand.social.twitter} />}
      </Helmet>
      {/* Header */}
      <div className="mb-8">
        <h1
          className="text-4xl font-bold text-primary mb-4 flex items-center gap-3"
          style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400, color: '#333334' } : undefined}
        >
          {isAhp ? <FileText className="w-10 h-10" style={{ color: '#2b5e49' }} /> : <Terminal className="w-10 h-10" />}
          {t('procedures:pageTitle')}
        </h1>
        <p className="text-lg text-muted-foreground">
          {isAhp
            ? t('procedures:pageSubtitle.ahp')
            : t('procedures:pageSubtitle.default')}
        </p>
      </div>

      {/* Filters */}
      <div className="bg-secondary/50 border border-border rounded-lg p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('procedures:filters.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-background"
            />
          </div>

          {/* Code Type Filter */}
          <Select value={selectedCodeType} onValueChange={setSelectedCodeType}>
            <SelectTrigger>
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder={t('procedures:filters.codeTypePlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('procedures:filters.allCodeTypes')}</SelectItem>
              {Object.entries(codeTypeCounts).map(([type, count]) => (
                <SelectItem key={type} value={type}>
                  {type} ({count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Minimum Hospitals Filter */}
          <Select value={minHospitals.toString()} onValueChange={(value) => setMinHospitals(parseInt(value))}>
            <SelectTrigger>
              <SelectValue placeholder={t('procedures:filters.minimumHospitals')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">{t('procedures:filters.hospitalsPlus', { count: 5 })}</SelectItem>
              <SelectItem value="10">{t('procedures:filters.hospitalsPlus', { count: 10 })}</SelectItem>
              <SelectItem value="25">{t('procedures:filters.hospitalsPlus', { count: 25 })}</SelectItem>
              <SelectItem value="50">{t('procedures:filters.hospitalsPlus', { count: 50 })}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Admin Controls */}
        {isAdmin && (
          <div className="mt-6 pt-6 border-t border-border">
            <div className="flex items-center gap-4">
              <h3 className="text-sm font-medium text-muted-foreground">Admin Controls</h3>
              <Button
                onClick={() => generateNamesMutation.mutate({ limit: 5, minHospitalCount: minHospitals })}
                disabled={generateNamesMutation.isPending}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                {generateNamesMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                Generate 5 Consumer Names
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Results Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-background border border-border rounded-lg p-4">
          <h3 className="text-sm font-medium text-muted-foreground">{t('procedures:summary.totalProcedures')}</h3>
          <p className="text-2xl font-bold text-foreground">{filteredProcedures.length}</p>
        </div>
        <div className="bg-background border border-border rounded-lg p-4">
          <h3 className="text-sm font-medium text-muted-foreground">{t('procedures:summary.avgHospitalsPerCode')}</h3>
          <p className="text-2xl font-bold text-foreground">
            {filteredProcedures.length > 0 
              ? Math.round(filteredProcedures.reduce((sum, p) => sum + p.hospital_count, 0) / filteredProcedures.length)
              : 0}
          </p>
        </div>
        <div className="bg-background border border-border rounded-lg p-4">
          <h3 className="text-sm font-medium text-muted-foreground">{t('procedures:summary.codeTypes')}</h3>
          <p className="text-2xl font-bold text-foreground">{Object.keys(codeTypeCounts).length}</p>
        </div>
        <div className="bg-background border border-border rounded-lg p-4">
          <h3 className="text-sm font-medium text-muted-foreground">{t('procedures:summary.avgMarkup')}</h3>
          <p className="text-2xl font-bold text-foreground">
            {filteredProcedures.length > 0 
              ? `${Math.round(filteredProcedures.reduce((sum, p) => sum + p.avg_markup_pct, 0) / filteredProcedures.length * 10) / 10}%`
              : '0%'}
          </p>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary mr-3" />
          <span className="text-lg text-muted-foreground">{t('procedures:loading')}</span>
        </div>
      )}

      {/* Procedures Table */}
      {!isLoading && (
        <div className="bg-background border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left py-4 px-6 font-semibold">{t('procedures:table.headers.codeType')}</th>
                  <th className="text-left py-4 px-6 font-semibold">{t('procedures:table.headers.procedureCode')}</th>
                  <th className="text-left py-4 px-6 font-semibold">{t('procedures:table.headers.consumerFriendly')}</th>
                  <th className="text-left py-4 px-6 font-semibold">{t('procedures:table.headers.description')}</th>
                  <th className="text-right py-4 px-6 font-semibold">{t('procedures:table.headers.hospitals')}</th>
                  <th className="text-right py-4 px-6 font-semibold">{t('procedures:table.headers.avgHospitalPrice')}</th>
                  <th className="text-right py-4 px-6 font-semibold">{t('procedures:table.headers.avgNationalPrice')}</th>
                  <th className="text-right py-4 px-6 font-semibold">{t('procedures:table.headers.avgMarkup')}</th>
                  {isAdmin && (
                    <th className="text-center py-4 px-6 font-semibold">{t('procedures:table.headers.markCommon')}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredProcedures.slice(0, displayLimit).map((procedure, index) => (
                  <tr 
                    key={procedure.procedure_code}
                    className={index % 2 === 0 ? 'bg-background' : 'bg-muted/20'}
                  >
                    <td className="py-4 px-6">
                      <Badge className={`font-mono text-xs ${getCodeTypeColor(procedure.code_type)}`}>
                        {procedure.code_type}
                      </Badge>
                    </td>
                    <td className="py-4 px-6">
                      <code className="text-sm font-mono bg-muted/30 px-2 py-1 rounded">
                        {procedure.procedure_code}
                      </code>
                    </td>
                    <td className="py-4 px-6">
                      {procedure.consumer_friendly_description ? (
                        <span className="inline-block bg-primary/20 text-primary px-2 py-1 rounded text-sm font-medium">
                          {procedure.consumer_friendly_description}
                        </span>
                      ) : isAdmin ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (procedure.procedure_id) {
                              generateIndividualNameMutation.mutate({
                                procedureId: procedure.procedure_id,
                                procedureCode: procedure.procedure_code,
                                description: procedure.description
                              });
                            }
                          }}
                          disabled={generateIndividualNameMutation.isPending}
                          className="text-xs text-muted-foreground hover:text-primary h-6 px-2"
                        >
                          {generateIndividualNameMutation.isPending ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            'Generate name'
                          )}
                        </Button>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </td>
                    <td className="py-4 px-6 text-sm text-muted-foreground max-w-md">
                      {procedure.description || t('common:common.noDescription')}
                    </td>
                    <td className="py-4 px-6 text-right font-mono">
                      {procedure.hospital_count}
                    </td>
                    <td className="py-4 px-6 text-right font-mono">
                      ${procedure.avg_hospital_price.toLocaleString()}
                    </td>
                    <td className="py-4 px-6 text-right font-mono text-muted-foreground">
                      ${procedure.avg_national_price.toLocaleString()}
                    </td>
                    <td className={`py-4 px-6 text-right font-mono font-bold ${getMarkupColor(procedure.avg_markup_pct)}`}>
                      {procedure.avg_markup_pct >= 0 ? '+' : ''}{procedure.avg_markup_pct}%
                    </td>
                    {isAdmin && (
                      <td className="py-4 px-6 text-center">
                        {procedure.procedure_id ? (
                          <Button
                            variant={procedure.is_common ? "default" : "outline"}
                            size="sm"
                            onClick={() => {
                              toggleCommonMutation.mutate({
                                procedureId: procedure.procedure_id!,
                                isCommon: !procedure.is_common
                              });
                            }}
                            disabled={toggleCommonMutation.isPending}
                            className="w-8 h-8 p-0"
                          >
                            {procedure.is_common ? (
                              <Star className="w-4 h-4 fill-current" />
                            ) : (
                              <StarOff className="w-4 h-4" />
                            )}
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">No ID</span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredProcedures.length > displayLimit && (
              <div className="flex justify-center py-4">
                <Button variant="outline" onClick={() => setDisplayLimit(l => l + 200)}>
                  Show more ({(filteredProcedures.length - displayLimit).toLocaleString()} remaining)
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {!isLoading && filteredProcedures.length === 0 && (
        <div className="text-center py-12">
          <p className="text-lg text-muted-foreground">{t('procedures:empty.noResults')}</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => {
              setSearchQuery('');
              setSelectedCodeType('all');
            }}
          >
            {t('procedures:empty.clearFilters')}
          </Button>
        </div>
      )}
    </div>
  );
}